import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Laptop, ChevronDown } from 'lucide-react';
import { useThemeMode } from '../context/ThemeModeContext';

export default function ThemeToggle({ showDropdown = false, className = '' }) {
  const { themeMode, isDark, setThemeMode, toggleThemeMode } = useThemeMode();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  if (showDropdown) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid="header-theme-toggle-dropdown-btn"
          title={`Current Theme: ${themeMode === 'system' ? 'System (' + (isDark ? 'Dark' : 'Light') + ')' : (isDark ? 'Dark' : 'Light')}`}
          className="flex items-center gap-1.5 p-2 rounded-xl text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
        >
          {isDark ? (
            <Moon className="w-4 h-4 text-indigo-400 animate-fade-in" />
          ) : (
            <Sun className="w-4 h-4 text-amber-500 animate-fade-in" />
          )}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>

        {open && (
          <div
            className="absolute right-0 mt-2 w-36 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-100 dark:border-slate-800 py-1.5 z-50 animate-scale-in"
            data-testid="theme-dropdown-menu"
          >
            <button
              type="button"
              onClick={() => { setThemeMode('light'); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors ${
                themeMode === 'light'
                  ? 'bg-amber-50 dark:bg-slate-800 text-amber-700 dark:text-amber-400 font-semibold'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
              }`}
            >
              <Sun className="w-3.5 h-3.5 text-amber-500" />
              <span>Light</span>
            </button>
            <button
              type="button"
              onClick={() => { setThemeMode('dark'); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors ${
                themeMode === 'dark'
                  ? 'bg-indigo-50 dark:bg-slate-800 text-indigo-700 dark:text-indigo-400 font-semibold'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
              }`}
            >
              <Moon className="w-3.5 h-3.5 text-indigo-400" />
              <span>Dark</span>
            </button>
            <button
              type="button"
              onClick={() => { setThemeMode('system'); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors ${
                themeMode === 'system'
                  ? 'bg-emerald-50 dark:bg-slate-800 text-emerald-700 dark:text-emerald-400 font-semibold'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
              }`}
            >
              <Laptop className="w-3.5 h-3.5 text-emerald-500" />
              <span>System</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // Fast One-Click Toggle Mode
  return (
    <button
      type="button"
      onClick={toggleThemeMode}
      data-testid="header-theme-toggle-btn"
      title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      className={`p-2 rounded-xl text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer relative group ${className}`}
    >
      <div className="relative w-4 h-4 flex items-center justify-center">
        {isDark ? (
          <Sun className="w-4 h-4 text-amber-400 group-hover:rotate-90 group-hover:scale-110 transition-transform duration-300" />
        ) : (
          <Moon className="w-4 h-4 text-slate-600 group-hover:-rotate-12 group-hover:text-indigo-600 group-hover:scale-110 transition-transform duration-300" />
        )}
      </div>
    </button>
  );
}
